import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { Polygon } from "react-leaflet";
import axios from "../../../../services/axios";
import Constants from "../../../../constants/Index";
import { ICoords } from "../../../../@types/GoogleMaps/ICoord";
import { useEditOnboarding } from "../../../../hooks/useEditOnboarding";
import { useFilter } from "../../../../hooks/useFilter";
import { AlertModal } from "../../../Modal/Alert/Index";
import { Modal } from "../../../Modal/Default/Index";
import { ButtonRedirect } from "../../../Modal/Error/ButtonRedirect/Index";
import { ButtonOnlyTitle } from "../../../Buttons/ButtonOnlyTitle/Index";
import { RoundedCloseButton } from "../../../Buttons/RoundedCloseButton/Index";
import { TitleModal } from "../../../Modal/Contents/TitleModal/Index";
import { TextModal } from "../../../Modal/Contents/TextModal/Index";
import { SimpleButtonClose } from "../../../Modal/Contents/SimpleButtonClose/Index";
import { FlyToBounds, FlyToBoundsRefProps } from "../../../Leaflet/FlyToBounds/Index";
import { LeafletStaticMap } from "../../../Leaflet/LeafletStaticMap/Index";
import { OptionV2 } from "../../../FormControlsV2";
import { FooterEditOnboarding } from "../../../EditOnboarding/Footer";
import { Loading } from "../../../Loading/Index";
import { useTranslation } from "react-i18next";
import {
  PastureManagementFormErrorsV2,
  PastureManagementFormV2,
  PastureManagementFormValueV2,
} from "../../../FormsV2";
import {
  normalizeMaxRate,
  normalizeRatingValues,
} from "../../../FormsV2/pastureManagementRating";
import "./styles.css";
import { getLocalizedApiName } from "../../../../utils/i18n/getLocalizedApiName";

const weekDays = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function createFrequencyOptions(t: (key: string) => string): OptionV2[] {
  return [
    { label: t("formsV2.frequencies.weekly"), value: 1, info: "WEEKLY" },
    { label: t("formsV2.frequencies.biweekly"), value: 2, info: "BIWEEKLY" },
    { label: t("formsV2.frequencies.monthly"), value: 3, info: "MONTHLY" },
  ];
}

function createEmptyValue(): PastureManagementFormValueV2 {
  return {
    farm: null,
    area: null,
    microAreas: [],
    type: null,
    monitor: null,
    heightInput: 0,
    heightOutput: 0,
    occupationDays: 0,
    restDays: 0,
    maxRate: 10,
    rateIn: 0,
    rateOut: 0,
    range: 0,
    executionPerDay: 0,
    frequency: null,
  };
}

function normalizeCoords(coords?: any): ICoords {
  if (Array.isArray(coords)) return coords as ICoords;
  if (Array.isArray(coords?.coords)) return coords.coords;
  return [];
}

function getOptionByValue(options: OptionV2[] = [], value: any) {
  return options.find((option) => String(option.value) === String(value)) || null;
}

function getOptionDomainName(option?: OptionV2 | null) {
  return (option as any)?.domainName || option?.label || "";
}

function toOption(value: any, language: string): OptionV2 | null {
  if (!value) return null;
  if ("label" in value && "value" in value) return value as OptionV2;
  if ("name" in value && "id" in value) {
    return { label: getLocalizedApiName(value.name, language), value: value.id, info: value };
  }
  return null;
}

function getMonitorOptions(type?: OptionV2 | null): OptionV2[] {
  if (!type || !Array.isArray(type.info)) return [];
  return type.info.map((monitor: any) => ({
    label: monitor.name,
    value: monitor.id,
    info: monitor,
    domainName: monitor.domainName || monitor.name,
  }));
}

function localizeManagementTypes(types: any[], language: string): OptionV2[] {
  return types.map((type: any) => ({
    label: getLocalizedApiName(type.name, language),
    value: type.id,
    info: type.monitoring_types.map((monitor: any) => ({
      ...monitor,
      name: getLocalizedApiName(monitor.name, language),
      domainName: getLocalizedApiName(monitor.name, "pt-BR"),
    })),
    domainName: getLocalizedApiName(type.name, "pt-BR"),
  }));
}

function parseApiDate(value?: string) {
  if (!value) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
}

function getSelectedDaysFromDefault(days?: number[]) {
  const selected = new Array(weekDays.length).fill(false);
  days?.forEach((day) => {
    const index = day === 7 ? 0 : day;
    if (index >= 0 && index < selected.length) selected[index] = true;
  });
  return selected;
}

function needsSchedule(value: PastureManagementFormValueV2) {
  return !!value.type && getOptionDomainName(value.type) !== "Sem monitoramento";
}

function needsFrequency(value: PastureManagementFormValueV2) {
  return getOptionDomainName(value.monitor) === "Altura" || getOptionDomainName(value.monitor) === "Nota";
}

function buildDefaultFormValue(
  defaultValue: any,
  farms: OptionV2[],
  areas: OptionV2[],
  microAreas: OptionV2[],
  managementTypes: OptionV2[],
  language: string,
  frequencies: OptionV2[]
): PastureManagementFormValueV2 {
  const type = getOptionByValue(managementTypes, defaultValue?.type?.value) || toOption(defaultValue?.type, language);
  const monitorOptions = getMonitorOptions(type);
  const monitor = getOptionByValue(monitorOptions, defaultValue?.monitor?.value) || toOption(defaultValue?.monitor, language);
  const farm = getOptionByValue(farms, defaultValue?.farm?.id || defaultValue?.farm_id) || toOption(defaultValue?.farm, language);
  const area = getOptionByValue(areas, defaultValue?.area?.id || defaultValue?.area_id) || toOption(defaultValue?.area, language);
  const selectedMicroAreas = (defaultValue?.microAreas || [])
    .map((microArea: any) => getOptionByValue(microAreas, microArea?.value || microArea?.id) || toOption(microArea, language))
    .filter(Boolean) as OptionV2[];

  const nextValue = createEmptyValue();
  nextValue.type = type;
  nextValue.monitor = monitor;
  nextValue.farm = farm;
  nextValue.area = area;
  nextValue.microAreas = selectedMicroAreas;
  nextValue.frequency =
    frequencies.find((frequency) => frequency.info === defaultValue?.frequency) || null;

  if (getOptionDomainName(type) === "Em faixa") {
    nextValue.range = Number(defaultValue?.requirement_in || 0);
    nextValue.executionPerDay = Number(defaultValue?.execution_per_day || 0);
  } else if (getOptionDomainName(monitor) === "Altura") {
    nextValue.heightInput = Number(defaultValue?.requirement_in || 0);
    nextValue.heightOutput = Number(defaultValue?.requirement_out || 0);
  } else if (getOptionDomainName(monitor) === "Nota") {
    Object.assign(
      nextValue,
      normalizeRatingValues(
        defaultValue?.max_rate,
        defaultValue?.requirement_in,
        defaultValue?.requirement_out
      )
    );
  } else if (getOptionDomainName(monitor) === "Dias") {
    nextValue.occupationDays = Number(defaultValue?.requirement_in || 0);
    nextValue.restDays = Number(defaultValue?.requirement_out || 0);
  }

  return nextValue;
}

export default function CreatePastureManagement() {
  const { t, i18n } = useTranslation();
  const { defaultValue, setOptionForm } = useEditOnboarding();
  const { setSystemManager, pastureManagementHistory } = useFilter();
  const flytoBoundsRef = useRef<FlyToBoundsRefProps>();
  const [managementTypesData, setManagementTypesData] = useState<any[]>([]);
  const [monitorTypes, setMonitorTypes] = useState<OptionV2[]>([]);
  const [microAreaOptions, setMicroAreaOptions] = useState<OptionV2[]>([]);
  const [farms, setFarms] = useState<OptionV2[]>([]);
  const [areas, setAreas] = useState<OptionV2[]>([]);
  const [fitCoords, setFitCoords] = useState<ICoords>([]);
  const [currentFarm, setCurrentFarm] = useState<any>();
  const [currentArea, setCurrentArea] = useState<any>();
  const [selectedDays, setSelectedDays] = useState<boolean[]>(new Array(weekDays.length).fill(false));
  const [disabledDays, setDisabledDays] = useState<number[]>([7]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [formValue, setFormValue] = useState<PastureManagementFormValueV2>(() => createEmptyValue());
  const [errors, setErrors] = useState<PastureManagementFormErrorsV2>({});
  const [msgError, setMsgError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(!!defaultValue?.id);
  const [confirmInactivationVisible, setConfirmInactivationVisible] = useState(false);

  const managementTypes = useMemo(
    () => localizeManagementTypes(managementTypesData, i18n.language),
    [managementTypesData, i18n.language]
  );
  const frequencyOptions = useMemo(
    () => createFrequencyOptions(t),
    [t, i18n.language]
  );

  const contentStyleAlert = {
    width: "335px",
    height: "237px",
    borderRadius: "20px",
    padding: "20px",
  } as CSSProperties;

  const filteredAreas = useMemo(
    () =>
      areas
        .filter((area: any) => !formValue.farm || String(area?.info?.farm?.id) === String(formValue.farm.value))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [areas, formValue.farm]
  );

  const filteredMicroAreas = useMemo(
    () =>
      microAreaOptions
        .filter((microArea: any) => !formValue.area || String(microArea?.info?.area_id) === String(formValue.area.value))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [formValue.area, microAreaOptions]
  );

  const selectedMicroAreaInfos = useMemo(
    () => formValue.microAreas.map((microArea) => microArea.info).filter(Boolean),
    [formValue.microAreas]
  );

  function updateFormValue(nextValue: PastureManagementFormValueV2) {
    setFormValue(nextValue);
    if (Object.keys(errors).length) validate(nextValue, startDate);
  }

  function validate(value = formValue, date = startDate) {
    const validation: PastureManagementFormErrorsV2 = {};

    if (!value.farm) validation.farm = t("internalValidation.farmRequired");
    if (!value.area) validation.area = t("internalValidation.areaRequired");
    if (!value.microAreas.length) validation.microAreas = t("internalValidation.paddocksRequired");
    if (!value.type) validation.type = t("internalValidation.grazingTypeRequired");

    if (value.type && getOptionDomainName(value.type) !== "Sem monitoramento") {
      if (getOptionDomainName(value.type) !== "Em faixa" && !value.monitor) {
        validation.monitor = t("internalValidation.monitoringTypeRequired");
      }

      if (getOptionDomainName(value.monitor) === "Altura") {
        if (!value.heightInput) validation.heightInput = t("internalValidation.inputHeightRequired");
        if (!value.heightOutput) validation.heightOutput = t("internalValidation.outputHeightRequired");
      }

      if (getOptionDomainName(value.monitor) === "Dias") {
        if (!value.occupationDays) validation.occupationDays = t("internalValidation.occupationDaysRequired");
        if (!value.restDays) validation.restDays = t("internalValidation.restDaysRequired");
      }

      if (getOptionDomainName(value.monitor) === "Nota") {
        const maxRate = normalizeMaxRate(value.maxRate);
        if (![5, 10].includes(value.maxRate)) {
          validation.maxRate = t("internalValidation.selectRatingScale");
        }
        if (value.rateIn < 1 || value.rateIn > maxRate) {
          validation.rateIn = t("internalValidation.inputRatingRange", { max: maxRate });
        }
        if (value.rateOut < 1 || value.rateOut > maxRate) {
          validation.rateOut = t("internalValidation.outputRatingRange", { max: maxRate });
        } else if (value.rateOut > value.rateIn) {
          validation.rateOut = t("internalValidation.outputRatingLimit");
        }
      }

      if (getOptionDomainName(value.type) === "Em faixa") {
        if (!value.range) validation.range = t("internalValidation.quantityRequired");
        if (!value.executionPerDay) validation.executionPerDay = t("internalValidation.executionsRequired");
      }

      if (needsFrequency(value) && !value.frequency) {
        validation.frequency = t("internalValidation.frequencyRequired");
      }

      if (needsSchedule(value) && !date) {
        validation.startDate = t("internalValidation.selectStartDate");
      }
    }

    setErrors(validation);
    return Object.keys(validation).length === 0;
  }

  function toggleDay(index: number) {
    setSelectedDays((current) => {
      if (formValue.frequency?.value === 2) {
        const nextSelected = new Array(weekDays.length).fill(false);
        nextSelected[index] = true;
        return nextSelected;
      }

      const nextSelected = [...current];
      nextSelected[index] = !nextSelected[index];
      return nextSelected;
    });
  }

  function handleManagementTypeChange(type: OptionV2 | null) {
    const nextMonitorTypes = getMonitorOptions(type);
    setMonitorTypes(nextMonitorTypes);
    setSelectedDays(new Array(weekDays.length).fill(false));
    setStartDate(undefined);
  }

  function handleMonitorTypeChange(monitor: OptionV2 | null) {
    if (getOptionDomainName(monitor) === "Dias") {
      setSelectedDays(new Array(weekDays.length).fill(false));
    }
  }

  function handleFrequencyChange(frequency: OptionV2 | null) {
    if (frequency?.value === 2 || frequency?.value === 3) {
      setSelectedDays(new Array(weekDays.length).fill(false));
    }
  }

  function buildBodyPost() {
    const microAreaIds = formValue.microAreas.map((microArea) => Number(microArea.value));

    let bodyPost = {} as any;
    if (getOptionDomainName(formValue.type) === "Sem monitoramento") {
      bodyPost = {
        micro_area_ids: microAreaIds,
        management_type_id: Number(formValue.type?.value),
      };
    } else {
      let requirements = {
        requirement_in: null,
        requirement_out: null,
      } as any;

      if (getOptionDomainName(formValue.monitor) === "Altura") {
        requirements = {
          requirement_in: formValue.heightInput,
          requirement_out: formValue.heightOutput,
        };
      } else if (getOptionDomainName(formValue.monitor) === "Nota") {
        requirements = {
          max_rate: normalizeMaxRate(formValue.maxRate),
          requirement_in: formValue.rateIn,
          requirement_out: formValue.rateOut,
        };
      } else if (getOptionDomainName(formValue.monitor) === "Dias") {
        requirements = {
          requirement_in: formValue.occupationDays,
          requirement_out: formValue.restDays,
        };
      } else if (getOptionDomainName(formValue.type) === "Em faixa") {
        requirements = {
          requirement_in: formValue.range,
          requirement_out: null,
        };
      }

      bodyPost = {
        micro_area_ids: microAreaIds,
        management_type_id: Number(formValue.type?.value),
        monitoring_type_id: Number(formValue.monitor?.value),
        ...requirements,
        frequency: formValue.frequency?.info,
        days_of_week: selectedDays
          .map((day, index) => (day ? (index === 0 ? 7 : index) : -1))
          .filter((day) => day !== -1),
        execution_per_day: formValue.executionPerDay || null,
        start_date: startDate ? format(startDate, "yyyy-MM-dd") : undefined,
      };

      if (defaultValue?.id) {
        bodyPost.management_group_id = defaultValue.id;
      }
    }

    return bodyPost;
  }

  function postManagement(bodyPost: any) {
    setSubmitting(true);
    axios
      .post("/micro-area-management", bodyPost)
      .then(() => {
        setOptionForm(undefined);
        setSystemManager(null);
      })
      .catch((err) => setMsgError(err.response?.data?.msg || t("internalValidation.createManagementError")))
      .finally(() => setSubmitting(false));
  }

  function submitForm() {
    if (submitting || pastureManagementHistory) return;
    if (!validate()) return;

    postManagement(buildBodyPost());
  }

  function deleteManagement() {
    if (submitting || pastureManagementHistory || !defaultValue?.id) return;

    setConfirmInactivationVisible(false);
    postManagement({
      ...buildBodyPost(),
      management_type_id: 1,
    });
  }

  useEffect(() => {
    let active = true;
    setLoadingEdit(!!defaultValue?.id);

    Promise.all([
      axios.get("micro-area-management/types"),
      axios.get("/farms/user"),
      axios.get("/areas/user"),
      axios.get("/micro-areas"),
    ])
      .then(([managementTypesResponse, farmsResponse, areasResponse, microAreasResponse]) => {
        if (!active) return;

        const nextManagementTypes = localizeManagementTypes(managementTypesResponse.data, i18n.language);
        const nextFarms = farmsResponse.data.map((farm: any) => ({
          label: farm.name,
          value: farm.id,
          info: farm,
        }));
        const nextAreas = areasResponse.data.map((area: any) => ({
          label: area.name,
          value: area.id,
          info: area,
        }));
        const nextMicroAreas = microAreasResponse.data.map((microArea: any) => ({
          label: `${microArea.farm.initials}-${microArea.area.initials}-${microArea.initials}`,
          value: microArea.id,
          info: microArea,
        }));

        setManagementTypesData(managementTypesResponse.data);
        setFarms(nextFarms);
        setAreas(nextAreas);
        setMicroAreaOptions(nextMicroAreas);

        if (defaultValue) {
          const nextValue = buildDefaultFormValue(
            defaultValue,
            nextFarms,
            nextAreas,
            nextMicroAreas,
            nextManagementTypes,
            i18n.language,
            frequencyOptions
          );
          setFormValue(nextValue);
          setMonitorTypes(getMonitorOptions(nextValue.type));
          setCurrentFarm(nextValue.farm?.info || defaultValue.farm);
          setCurrentArea(nextValue.area?.info || defaultValue.area);
          setSelectedDays(getSelectedDaysFromDefault(defaultValue.days_of_week));
          setStartDate(parseApiDate(defaultValue.start_date));
        }
      })
      .catch((err) => {
        if (active) setMsgError(err.response?.data?.msg || t("internalValidation.loadManagementError"));
      })
      .finally(() => {
        if (active) setLoadingEdit(false);
      });

    return () => {
      active = false;
    };
  }, [defaultValue]);

  useEffect(() => {
    if (currentFarm?.map_coords) {
      setFitCoords(normalizeCoords(currentFarm.map_coords));
    }
  }, [currentFarm]);

  useEffect(() => {
    if (!managementTypes.length || !formValue.type) return;

    const localizedType = getOptionByValue(managementTypes, formValue.type.value);
    if (!localizedType) return;

    const localizedMonitorTypes = getMonitorOptions(localizedType);
    const localizedMonitor = formValue.monitor
        ? getOptionByValue(localizedMonitorTypes, formValue.monitor.value)
        : null;

    setMonitorTypes(localizedMonitorTypes);
    setFormValue((current) => ({
      ...current,
      type: localizedType,
      monitor: localizedMonitor,
    }));
    // formValue is intentionally omitted: this synchronization must run only
    // when the localized option collection is rebuilt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managementTypes]);

  useEffect(() => {
    setFormValue((current) => {
      if (!current.frequency) return current;
      const localizedFrequency = frequencyOptions.find(
        (frequency) => frequency.info === current.frequency?.info
      );
      return localizedFrequency ? { ...current, frequency: localizedFrequency } : current;
    });
  }, [frequencyOptions]);

  useEffect(() => {
    if (currentArea?.map_coords) {
      setFitCoords(normalizeCoords(currentArea.map_coords));
    }
  }, [currentArea]);

  useEffect(() => {
    if (formValue.frequency?.value === 3 || getOptionDomainName(formValue.monitor) === "Dias" || getOptionDomainName(formValue.type) === "Em faixa") {
      setDisabledDays([7]);
      return;
    }

    setDisabledDays(selectedDays.map((day, index) => (!day ? index : 7)));
    if (startDate && !selectedDays[startDate.getDay()]) {
      setStartDate(undefined);
    }
  }, [formValue.frequency, formValue.monitor, formValue.type, selectedDays, startDate]);

  const mapSlot = (
    <div className="pasture-management-v2__map-column">
      <div className="area__drawarea--container edit-onboarding-v2__map-preview">
        <LeafletStaticMap>
          <FlyToBounds ref={flytoBoundsRef} fitCoords={fitCoords}>
            {fitCoords.length > 0 && <Polygon positions={fitCoords} color={Constants.LEAFLET_UNFOCUS.color} />}
            {selectedMicroAreaInfos.map((micro: any) => (
              <Polygon
                key={micro.id || micro.uuid}
                positions={micro.map_coords || []}
                color={Constants.LEAFLET_UNFOCUS_TRANSLUCENT.color}
                fillColor={Constants.LEAFLET_NORMAL_BLUE.fillColor}
                fillOpacity={Constants.LEAFLET_UNFOCUS_TRANSLUCENT.fillOpacity}
              />
            ))}
          </FlyToBounds>
        </LeafletStaticMap>
      </div>
    </div>
  );

  return (
    <>
      <AlertModal visible={!!msgError} contentStyle={contentStyleAlert}>
        <section className="EditAreas_modalalert-container">
          <SimpleButtonClose onClick={() => setMsgError("")} />
          <main className="EditAreas_modalalert-content">
            <TitleModal>{t("managementUi.somethingWrong")}</TitleModal>
            <TextModal>{msgError}</TextModal>
          </main>
          <ButtonRedirect onClick={() => setMsgError("")}>{t("commonUi.back")}</ButtonRedirect>
        </section>
      </AlertModal>

      <Modal
        visible={confirmInactivationVisible}
        contentStyle={{
          width: "600px",
          height: "fit-content",
          borderRadius: 20,
          padding: "40px",
          position: "relative",
        }}
      >
        <div style={{ position: "absolute", top: 20, right: 20 }}>
          <RoundedCloseButton onClick={() => setConfirmInactivationVisible(false)} />
        </div>
        <section style={{ display: "flex", flexDirection: "column", gap: 30 }}>
          <header>
            <TitleModal>{t("managementDialogs.deactivateManagement")}</TitleModal>
          </header>
          <footer style={{ display: "flex", justifyContent: "flex-end", gap: 16 }}>
            <ButtonOnlyTitle
              title={t("commonUi.cancel")}
              theme="light"
              type="button"
              onClick={() => setConfirmInactivationVisible(false)}
            />
            <ButtonOnlyTitle
              title={t("taskRequests.confirm")}
              theme="info"
              type="button"
              disabled={submitting}
              onClick={deleteManagement}
            />
          </footer>
        </section>
      </Modal>

      <main className="body__formEditOnboarding body__formEditOnboarding--property-v2">
        <Loading visible={loadingEdit} contained />
        <PastureManagementFormV2
          value={formValue}
          errors={errors}
          farms={farms.sort((a, b) => a.label.localeCompare(b.label))}
          areas={filteredAreas}
          microAreas={filteredMicroAreas}
          managementTypes={managementTypes}
          monitorTypes={monitorTypes}
          frequencyOptions={frequencyOptions}
          selectedDays={selectedDays}
          weekDays={weekDays}
          disabledDays={disabledDays}
          startDate={startDate}
          endDate={parseApiDate(defaultValue?.end_date)}
          historyMode={pastureManagementHistory}
          mapSlot={mapSlot}
          onChange={updateFormValue}
          onFarmChange={(farm) => {
            setCurrentFarm(farm?.info);
            setCurrentArea(undefined);
            setFitCoords(normalizeCoords(farm?.info?.map_coords));
          }}
          onAreaChange={(area) => {
            setCurrentArea(area?.info);
            setFitCoords(normalizeCoords(area?.info?.map_coords));
          }}
          onMicroAreasChange={() => undefined}
          onManagementTypeChange={handleManagementTypeChange}
          onMonitorTypeChange={handleMonitorTypeChange}
          onFrequencyChange={handleFrequencyChange}
          onDayToggle={toggleDay}
          onStartDateChange={(date) => {
            setStartDate(date);
            if (errors.startDate) validate(formValue, date);
          }}
        />
      </main>

      {!pastureManagementHistory && (
        <FooterEditOnboarding
          undoFunction={() => setOptionForm(undefined)}
          saveFunction={submitForm}
          deleteFunction={defaultValue?.id ? () => setConfirmInactivationVisible(true) : undefined}
          isNew={!defaultValue?.id}
          disabledSubmit={submitting}
        />
      )}
    </>
  );
}
